/**
 * UniverseSwitcher — Top utility banner: HOURS · ADDRESS · CTA cluster.
 * On mobile in jardinerie mode, the "Devis gratuit" CTA is replaced by
 * the account + cart icons (freeing space in the main header for the
 * universe toggle pill).
 *
 * NON-CORE FILE — additive customization.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, ChevronDown, ArrowRight, ShoppingCart, User } from "lucide-react";
import { useUniverse } from "@/lib/jardins-ole/useUniverse";
import { useSettings, isOpenNow } from "@/lib/useSettings";
import { useCart } from "@/lib/useCart";
import { useCustomerAuth } from "@/lib/useCustomerAuth";
import { track } from "@/lib/api";
import HoursDropdownContent from "../public/HoursDropdownContent";

export default function UniverseSwitcher() {
  const { enabled, universe } = useUniverse();
  const { settings: s } = useSettings();
  const { count: cartCount, setDrawerOpen: setCartOpen } = useCart();
  const { customer, isAuthenticated } = useCustomerAuth();
  const [hoursOpen, setHoursOpen] = useState(false);
  const hoursRef = useRef(null);

  useEffect(() => {
    const onClickAway = (e) => {
      if (hoursRef.current && !hoursRef.current.contains(e.target)) setHoursOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setHoursOpen(false);
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  if (!enabled) return null;

  const status = isOpenNow(s?.hours || []);
  const todayIdx = (new Date().getDay() + 6) % 7;
  const address = s?.address_public || "Route de Miramas, Istres";
  const mapsHref = s?.google_maps_url || `https://www.google.com/maps/search/${encodeURIComponent(address)}`;
  const showShopIcons = !!s?.shop_enabled && universe === "jardinerie";

  return (
    <div
      className="jdo-universe-banner sticky top-0 z-[60] w-full backdrop-blur-md border-b"
      data-testid="universe-switcher"
    >
      <div className="max-w-3xl mx-auto px-4 md:px-6 h-10 md:h-11 flex items-center justify-between gap-3">
        {/* LEFT — Hours pill */}
        <div className="relative flex items-center" ref={hoursRef}>
          <button
            type="button"
            onClick={() => setHoursOpen((o) => !o)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] md:text-[11px] font-bold tracking-wide whitespace-nowrap transition-all ${
              status.open
                ? "bg-emerald-400/20 text-emerald-100 border border-emerald-300/40 hover:bg-emerald-400/30"
                : "bg-amber-400/20 text-amber-100 border border-amber-300/40 hover:bg-amber-400/30"
            }`}
            data-testid="jdo-banner-hours"
            aria-expanded={hoursOpen}
          >
            <Clock className="w-3 h-3" strokeWidth={2.6} />
            <span>{status.label}</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${hoursOpen ? "rotate-180" : ""}`} />
          </button>
          {hoursOpen && (
            <div
              className="absolute left-0 top-full mt-2 w-80 max-w-[92vw] bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden z-[70] animate-fade-up"
              data-testid="jdo-hours-dropdown"
            >
              <HoursDropdownContent s={s || {}} todayIdx={todayIdx} status={status} />
            </div>
          )}
        </div>

        {/* CENTER — Address (desktop only) */}
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("address_click", { meta: { location: "jdo-banner" } })}
          className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/80 hover:text-[#E8C97A] transition-colors whitespace-nowrap"
          data-testid="jdo-banner-address"
        >
          <MapPin className="w-3.5 h-3.5" strokeWidth={2.4} />
          <span>{address}</span>
        </a>

        {/* RIGHT cluster */}
        <div className="flex items-center gap-2">
          {/* Mobile + jardinerie: account + cart icon buttons (replace Devis CTA) */}
          {showShopIcons && (
            <div className="md:hidden flex items-center gap-1.5" data-testid="jdo-banner-mobile-shop-icons">
              <Link
                to={isAuthenticated ? "/account" : "/account/login"}
                className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/25 transition-colors"
                aria-label={isAuthenticated ? `Mon compte (${customer?.first_name || customer?.email})` : "Se connecter"}
                data-testid="jdo-banner-account"
              >
                <User className="w-4 h-4" strokeWidth={2.4} />
                {isAuthenticated && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[var(--jdo-blue,#1E3A5F)]" />
                )}
              </Link>
              <button
                type="button"
                onClick={() => setCartOpen(true)}
                className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/25 transition-colors"
                aria-label={`Panier ${cartCount} article${cartCount > 1 ? "s" : ""}`}
                data-testid="jdo-banner-cart"
              >
                <ShoppingCart className="w-4 h-4" strokeWidth={2.4} />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[color:var(--brand-red)] text-white text-[9px] font-bold flex items-center justify-center shadow"
                    data-testid="jdo-banner-cart-count"
                  >
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Devis CTA — always visible on desktop, only on mobile when paysagiste (no shop) */}
          <Link
            to="/devis"
            className={`jdo-banner-devis-btn inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full font-extrabold text-[11px] tracking-wide whitespace-nowrap shadow-md transition-all ${
              showShopIcons ? "hidden md:inline-flex" : ""
            }`}
            data-testid="jdo-banner-devis"
          >
            <span>Devis gratuit</span>
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </div>
  );
}
